package com.fireavert.react.shared.components.nav

import com.fireavert.cache.client.Preferences
import com.fireavert.menu.client.presentation.MenuController
import com.fireavert.react.shared.SearchableDropDownList
import com.fireavert.react.shared.SelectorDropDown
import com.fireavert.react.shared.styles.ModernNavStyles
import com.fireavert.utilities.toLocalDate
import emotion.css.ClassName
import emotion.css.cx
import emotion.react.css
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.LocalDate
import react.FC
import react.Props
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.img
import web.cssom.*
import kotlin.js.Date
import kotlin.time.Duration.Companion.days

external interface GlobalFiltersProps : Props {
    var coroutineScope: CoroutineScope
    var controller: MenuController
    var preferences: Preferences
    var propertyMapState: Map<String, Int?>
    var defaultPropertyValueState: Int?
    var isCollapsed: Boolean
    var disableTimeSelector: Boolean
}

val GlobalFilters = FC<GlobalFiltersProps> { props ->
    div {
        className = cx(
            ModernNavStyles.filtersSection,
            ModernNavStyles.muiDropdownFix
        )
        ariaLabel = "Global Filters"
        css {
            if (props.isCollapsed) {
                display = None.none
            }
            padding = important(Padding(16.px, 16.px))
        }

        div {
            className = ModernNavStyles.filterLabel
            +"PROPERTY & TIME FILTERS"

            // Icon before text
            img {
                src = "/static/calendar_icon.svg"
                css {
                    width = 16.px
                    height = 16.px
                }
            }
        }

        // Property filter dropdown
        div {
            className = ClassName {
                backgroundColor = important(NamedColor.white)
                borderRadius = 4.px
            }

            // Make sure propertyMapState always has "All" mapped correctly
            val enhancedPropertyMap = if (props.propertyMapState.containsKey("All")) {
                props.propertyMapState
            } else {
                mapOf("All" to null) + props.propertyMapState
            }

            // Determine the correct default value to pass
            val effectiveDefaultValue = when(props.defaultPropertyValueState) {
                null, 0 -> null  // Treat both null and 0 as "All"
                else -> props.defaultPropertyValueState
            }

            SearchableDropDownList {
                optionMap = enhancedPropertyMap
                mobileVersion = false
                disabled = false
                defaultValue = effectiveDefaultValue
                onChange = { selectedPair -> // Pair<Int?, String?>
                    val selectedId = selectedPair.first
                    props.coroutineScope.launch {
                        props.controller.updatePropertyPreferences(selectedId, window.location.pathname)
                    }
                }
            }
        }

        div {
            // Divider
            className = ModernNavStyles.divider
        }

        // Time period dropdown
        div {
            className = ClassName {
                backgroundColor = important(NamedColor.white)
                borderRadius = 4.px
            }

            // Create the time period options map
            val timePeriodOptions = mapOf(
                "Last 24 Hours" to 1,
                "Last 7 Days" to 7,
                "Last 30 Days" to 30,
                "Last 90 Days" to 92,
                "Last 365 Days" to 365,
            )

            // Find the current selected period based on preferences
            val initialTimePeriodSelection = findMatchingDateOption(props.preferences, timePeriodOptions)

            SelectorDropDown {
                optionMap = timePeriodOptions
                mobileVersion = false
                disabled = props.disableTimeSelector
                defaultSelection = initialTimePeriodSelection
                onChange = { selectedTime ->
                    val now = Clock.System.now()
                    val duration = selectedTime?.days ?: 1.days
                    val startInstant = now - duration

                    val finalDate = Date(startInstant.toEpochMilliseconds()).toLocalDate()
                    val endDateSelector = Date(now.toEpochMilliseconds()).toLocalDate()
                    props.coroutineScope.launch {
                        props.controller.updatedDateRangePreferences(finalDate, endDateSelector, window.location.pathname)
                    }
                }
            }
        }
    }
}

private fun findMatchingDateOption(preferences: Preferences, optionMap: Map<String, Int?>): String {
    val startDate = preferences.globalStartDate
    val endDate = preferences.globalEndDate

    // If no dates are set, return the first option (likely "Last 24 Hours")
    if (startDate == null || endDate == null) {
        return optionMap.keys.firstOrNull() ?: ""
    }

    // Calculate days between dates
    val diffDays = calculateDaysBetween(startDate, endDate)

    // Find the closest matching option
    return when {
        diffDays <= 2 -> "Last 24 Hours"
        diffDays <= 8 -> "Last 7 Days"
        diffDays <= 32 -> "Last 30 Days"
        diffDays <= 94 -> "Last 90 Days"
        else -> "Last 365 Days"
    }
}

// Helper function to calculate days between LocalDate objects
private fun calculateDaysBetween(startDate: LocalDate, endDate: LocalDate): Int {
    // Use kotlinx.datetime's functions to calculate duration between dates
    val startDay = startDate.toEpochDays()
    val endDay = endDate.toEpochDays()
    return endDay - startDay + 1 // +1 to include both start and end dates
}

